/**
 * Utility functions for interacting with the storage service
 */

const STORAGE_SERVICE_URL = process.env.REACT_APP_STORAGE_SERVICE_URL;

/**
 * Check if the storage service is accessible
 * @returns Promise<boolean> - true if service is accessible, false otherwise
 */
export const checkStorageServiceHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${STORAGE_SERVICE_URL}/api/v1/designs`, {
      method: 'GET',
      headers: {
        'accept': '*/*',
        'access-control-allow-origin': '*'
      }
    });
    
    const isHealthy = response.status < 500; // Any response that's not a server error means service is up
    return isHealthy;
  } catch (error) {
    console.error('storageServiceUtils: Storage service health check failed:', error);
    return false;
  }
};

/**
 * Get detailed error message based on storage service status and error
 * @param error - The error message from the fetch operation
 * @param isServiceHealthy - Whether the storage service is accessible
 * @returns Detailed error message for the user
 */
export const getDetailedErrorMessage = (error: string, isServiceHealthy: boolean): string => {
  if (!isServiceHealthy) {
    return 'The storage service is currently not accessible. Please check if the service is running on the expected port (57375) and try again.';
  }

  if (error.includes('Design not found') || error.includes('404')) {
    return 'The design file could not be found on the server.';
  }

  if (error.includes('Network Error') || error.includes('fetch')) {
    return 'Network connection error. Please check your internet connection and the storage service status.';
  }

  if (error.includes('403')) {
    return 'You do not have permission to access this design.';
  }

  if (error.includes('500')) {
    return 'Server error occurred while loading the design. Please try again later or contact support.';
  }

  return `An unknown error occurred while loading the design: ${error}`;
};
